import type { ReactNode } from 'react';
import { Logo } from '../brand/Logo.js';
import { Button } from '../components/Button.js';
import { Callout } from '../components/Callout.js';
import { cn } from '../lib/cn.js';

export interface AuthScreenProps {
  /** Optional product surface label shown top-right of the card (for example `Admin`); omit for the product itself. */
  surface?: string;
  title?: string;
  message: string;
  onSignIn: () => void;
  signInLabel?: string;
  loading?: boolean;
  /** Error text from the last attempt (denied, not invited, network). */
  error?: string;
  /** Mono footer (server URL, version, mode). */
  footer?: string;
  /** Extra content under the action (dev persona helper, status). */
  children?: ReactNode;
  className?: string;
}

/** Sign-in screen: single primary action that opens the system browser (OIDC PKCE). */
export function AuthScreen({ surface, title = 'Sign in', message, onSignIn, signInLabel = 'Sign in with Agency', loading = false, error, footer, children, className }: AuthScreenProps) {
  return (
    <main className={cn('flex min-h-screen w-full flex-col items-center justify-center bg-bg px-6 text-ink', className)}>
      <div className="w-full max-w-[400px] rounded-sm border border-line bg-panel">
        <div className="flex h-14 items-center justify-between border-b border-line px-5">
          <Logo height={18} />
          {surface ? <span className="label-mono">{surface}</span> : null}
        </div>
        <div className="px-5 py-5">
          <h1 className="text-2xl font-medium tracking-tight">{title}</h1>
          <p className="mt-2 text-body leading-relaxed text-ink/85">{message}</p>
          {error ? (
            <Callout tone="critical" className="mt-4">
              {error}
            </Callout>
          ) : null}
          <div className="mt-5 flex justify-end">
            <Button variant="primary" onClick={onSignIn} loading={loading}>
              {signInLabel}
            </Button>
          </div>
          {children ? <div className="mt-4">{children}</div> : null}
        </div>
        {footer ? <div className="border-t border-hair px-5 py-2.5 font-mono text-2xs text-mute">{footer}</div> : null}
      </div>
    </main>
  );
}
